import { useCallback, useMemo } from "react"
import { keepPreviousData } from "@tanstack/react-query"

import type { IVariantSummary } from "~/hooks/useVariantTableData"
import { api } from "~/utils/api"
import { useDemand2Scope } from "~/components/demand-2/useDemand2Scope"
import { useDemandScope } from "~/components/demand-2/useDemandScope"
import {
  useAttributableCollectionWide,
  useDemandAdjustments,
  useDemandForecasts,
} from "~/components/demand-2/useDemandIntents"
import {
  computeEffectiveDemand,
  type EffectiveCtx,
  type EffectiveDemand,
  type ServerTotals,
} from "~/utils/demandEffective"
import { type InsightOffer, type InsightScalars } from "~/utils/demandInsightValues"
import type { SeasonalityProfileBadgeInput } from "~/utils/demandSeasonalityProfile"
import {
  type DemandGrain,
  isCollectionWide,
  scopeToColumns,
  WHOLE_VARIANT_SCOPE,
} from "~/utils/demandScope"
import { type DraftAdjustment, type SalesLevel } from "~/components/demand-2/demandDraftState"

export type { EffectiveDemand }

const memoizeByObject = <K extends object, V>(fn: (key: K) => V) => {
  const cache = new WeakMap<K, V>()
  return (key: K): V => {
    const hit = cache.get(key)
    if (hit) return hit
    const next = fn(key)
    cache.set(key, next)
    return next
  }
}

export type SharedProps = {
  collectionId: number
  kind: "fashion" | "noos"
  variants: IVariantSummary[]
  salesLevel: SalesLevel
  // Fashion baseline compares against the predecessor collection; null falls back to own sales.
  sourceCollectionIds: number[] | null
}

/**
 * A variant's adjustment stack, collection-wide rows first, as the engine expands it.
 * `collectionWide` is exposed separately: editing one from a variant's panel moves every variant.
 */
export const useDraftAdjMap = (draftAdj: DraftAdjustment[]) => {
  const collectionWide = useAttributableCollectionWide()
  return useMemo(() => {
    const m = new Map<string, DraftAdjustment[]>()
    for (const a of draftAdj) {
      if (isCollectionWide(a)) continue
      const arr = m.get(a.variantId) ?? []
      arr.push(a)
      m.set(a.variantId, arr)
    }
    return {
      get: (variantId: string): DraftAdjustment[] | undefined =>
        collectionWide.length === 0
          ? m.get(variantId)
          : [...collectionWide, ...(m.get(variantId) ?? [])],
      collectionWide,
    }
  }, [draftAdj, collectionWide])
}

/**
 * Per-variant demand from the server reconstruction, shared by the Total table, reviewer KPI header
 * and budget view so they read one number.
 */
export const useEffectiveDemand = <G extends DemandGrain>({
  grain,
  insightOffers,
}: {
  // Budget passes "whole-variant": it reconciles against a whole-collection budget.
  grain: G
  /**
   * Sizes every insight offer. Part of the query key, and it lifts the gate on the engine's
   * DS-derived reads, so a view that never reads an offer must not set it.
   */
  insightOffers?: boolean
}): {
  effective: (v: IVariantSummary) => EffectiveDemand & { grain: G }
  isLoading: boolean
  isPlaceholderData: boolean
  /** Null when not asked for; `[]` when this variant genuinely offers nothing. */
  offersFor: (variantId: string) => InsightOffer[] | null
  /** Null on the same terms as `offersFor`. */
  scalarsFor: (variantId: string) => InsightScalars | null
  /** Null until the totals land. */
  seasonalityProfileFor: (variantId: string) => SeasonalityProfileBadgeInput | null
} => {
  const { collectionIds } = useDemand2Scope()
  const editScope = useDemandScope()
  const scope = grain === "whole-variant" ? WHOLE_VARIANT_SCOPE : editScope
  const scopeArgs = scopeToColumns(scope)
  const [draftAdj] = useDemandAdjustments()
  const [draftForecasts] = useDemandForecasts()
  const draftAdjByVariant = useDraftAdjMap(draftAdj)

  // No variant list: the server reads the whole collection, so page filters reuse the entry.
  const totalsQ = api.demand2.getDemandTotals.useQuery(
    // `|| undefined`, not the flag itself: React Query's key hash drops `undefined` but keeps `false`,
    // so `false` would mint a second cache entry and stop this sharing the read with callers that omit it.
    { collectionIds, ...scopeArgs, includeInsightOffers: insightOffers || undefined },
    {
      enabled: collectionIds.length > 0,
      placeholderData: keepPreviousData,
    },
  )
  const totalsByVariant = useMemo(() => {
    const m = new Map<string, ServerTotals>()
    for (const t of totalsQ.data ?? []) m.set(t.variantId, t)
    return m
  }, [totalsQ.data])

  // Stable identity, or the memos below churn the accessor → column defs and loop registration.
  const ctx: EffectiveCtx = useMemo(
    () => ({
      draftAdjByVariant,
      draftForecasts,
      totalsByVariant,
    }),
    [draftAdjByVariant, draftForecasts, totalsByVariant],
  )

  // Lazily memoised per variant: AG Grid calls the accessor per cell, sort, filter and aggregation,
  // but several instances mount per page and most are read conditionally. Keyed on the variant
  // OBJECT, so a new ctx or grain starts a fresh cache; eager composition would make unread
  // instances pay for the whole collection.
  const effective = useMemo(
    () => memoizeByObject((v: IVariantSummary) => ({ ...computeEffectiveDemand(v, ctx), grain })),
    [ctx, grain],
  )
  // isLoading is the initial fetch only (keepPreviousData), so the load gate never re-hides a
  // populated grid.
  // Off the same totals entry as the numbers, so an offer and its plan never come from two fetches.
  const offersFor = useCallback(
    (variantId: string) => totalsByVariant.get(variantId)?.insightOffers ?? null,
    [totalsByVariant],
  )
  const scalarsFor = useCallback(
    (variantId: string) => totalsByVariant.get(variantId)?.insightScalars ?? null,
    [totalsByVariant],
  )
  const seasonalityProfileFor = useCallback(
    (variantId: string) => totalsByVariant.get(variantId)?.seasonalityProfile ?? null,
    [totalsByVariant],
  )
  return {
    effective,
    isLoading: totalsQ.isLoading,
    isPlaceholderData: totalsQ.isPlaceholderData,
    offersFor,
    scalarsFor,
    seasonalityProfileFor,
  }
}
